import { LoginRequest, MSG, PASSKEY_MSG, SUPPORT_MSG, oauthErrorText, safeNextPath, type ChooseResponse, type LoginNoCompany, type Me } from "@genbaryoku/contract";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { z } from "zod";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { setMe } from "../auth.js";
import { ClosedNotice } from "../components/ClosedNotice.jsx";
import { OAuthButtons } from "../components/OAuthButtons.jsx";
import { PasskeyIcon, cancelPasskey, isPasskeyCancel, markJustLoggedIn, passkeyAssert, passkeyErrorText, passkeyLogin, passkeyLoginVerify, usePasskeySupport, type PasskeyLoginResult } from "../components/Passkey.jsx";
import { AuthCard, ErrorText, Field, Help, PasswordInput, guardComposingEnter, useFieldIds } from "../components/ui.jsx";
import { AddCompanyPanel } from "../core/auth/AddCompany.jsx";
import { useProduct } from "../lib/useProduct.js";
import { afterLoginPath, rememberLoginNext } from "../lib/autoLogout.js";

export function LoginPage() {
  const ids = useFieldIds(["mail", "pw", "keep"] as const);
  const P = useProduct();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | undefined>();
  /** 10回続けて失敗して30分ロック中（#111）：文の下に「パスワード再設定」 */
  const [locked, setLocked] = useState<string | null>(null);
  const loc = useLocation();
  // Google・Apple から戻って断られた（#112）：?oauth_error=CODE の文（URL の文字はそのまま出さない）
  const [sp] = useSearchParams();
  const oauthError = oauthErrorText(sp.get("oauth_error")) || undefined;
  // 自動ログアウト・セッション切れのあと（#189）：?next= の画面を覚える（ログイン・会社を選ぶ・Google／Apple の続きが使う）
  const next = safeNextPath(sp.get("next"));
  useEffect(() => rememberLoginNext(next), [next]);
  // 退会の直後（/settings/close から）と、退会中のメールでログインしようとしたとき（026）
  const justClosed = (loc.state as { closed?: { purgeAfter: string; email: string } } | null)?.closed;
  const [closed, setClosed] = useState<{ email: string; purgeAfter: string | null } | undefined>(justClosed ? { email: justClosed.email, purgeAfter: justClosed.purgeAfter } : undefined);
  // remember は入力では省けて（既定 true）、出力では必ず boolean。RHF には入力・出力の型を分けて渡す
  const form = useForm<z.input<typeof LoginRequest>, unknown, LoginRequest>({
    resolver: zodResolver(LoginRequest),
    defaultValues: { email: "", password: "", remember: true },
    shouldFocusError: true,
  });
  const firstError = form.formState.errors.email?.message ?? form.formState.errors.password?.message;
  /** E0-c：パスワードは合ったが、この製品の会社が無い（403 PRODUCT_MISMATCH の代わり）。同じメール・パスワードで会社を作れる */
  const [noCompany, setNoCompany] = useState<(LoginNoCompany & { password: string }) | undefined>();

  // パスキー（#113）：ボタン（Figma の「または」の下の先頭）と、メール欄の自動入力（Conditional UI。対応するブラウザだけ）
  const pk = usePasskeySupport();
  const [pkBusy, setPkBusy] = useState(false);
  /** 自動入力の待ちを張り直す合図（ボタンで取りやめた・4分半たった） */
  const [pkArm, setPkArm] = useState(0);
  const rememberRef = useRef(true);
  rememberRef.current = form.watch("remember") !== false;
  const onPasskeyResult = async (res: PasskeyLoginResult) => {
    if ("choose" in res && res.choose) {
      nav("/choose", { state: res });
      return;
    }
    await setMe(qc, res);
    nav(afterLoginPath(), { replace: true });
  };
  const onPasskeyError = (e: unknown) => {
    if (e instanceof ApiError && e.code === "AUTH_ACCOUNT_CLOSED") {
      setClosed({ email: String(e.problem.email ?? ""), purgeAfter: typeof e.problem.purgeAfter === "string" ? e.problem.purgeAfter : null });
      return;
    }
    if (!isPasskeyCancel(e)) setServerError(passkeyErrorText(e));
  };
  useEffect(() => {
    if (!pk.autofill) return;
    let alive = true;
    // 1歩目（候補を出して選ばれるのを待つ）が断られた・止めたときは文を出さない：パスキーの使えない端末は、ページを開いた瞬間に
    // NotSupportedError で断る（何も押していない人に赤い文を見せない）。文を出すのは、候補を選んだあとの確かめ（2歩目）の失敗だけ
    void passkeyAssert(true).then(
      (response) => {
        if (!alive) return;
        void passkeyLoginVerify(response, rememberRef.current)
          .then(async (res) => {
            if (alive) await onPasskeyResult(res);
          })
          .catch((e: unknown) => alive && onPasskeyError(e));
      },
      () => undefined,
    );
    // challenge の寿命（5分）の前に張り直す
    const t = window.setTimeout(() => setPkArm((k) => k + 1), 270_000);
    return () => {
      alive = false;
      window.clearTimeout(t);
      cancelPasskey();
    };
    // onPasskeyResult・onPasskeyError は毎回作られる（中で使う nav・qc は変わらない）
  }, [pk.autofill, pkArm]);
  const loginWithPasskey = async () => {
    setServerError(undefined);
    setLocked(null);
    setPkBusy(true);
    try {
      await onPasskeyResult(await passkeyLogin(rememberRef.current));
    } catch (e) {
      onPasskeyError(e);
      if (pk.autofill) setPkArm((k) => k + 1);
    } finally {
      setPkBusy(false);
    }
  };

  const submit = form.handleSubmit(async (v) => {
    setServerError(undefined);
    setLocked(null);
    try {
      const res = await api<Me | ChooseResponse | LoginNoCompany>("POST", "/auth/login", v);
      if ("noCompany" in res && res.noCompany) {
        setNoCompany({ ...res, password: v.password });
        return;
      }
      if ("choose" in res && res.choose) {
        // 2社以上：会社を選ぶ（040）。state で渡す（開き直したらログインからやり直し）
        markJustLoggedIn();
        nav("/choose", { state: { ...res, email: v.email } });
        return;
      }
      await setMe(qc, res);
      markJustLoggedIn();
      nav(afterLoginPath(), { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === "AUTH_EMAIL_NOT_VERIFIED") {
        nav("/signup/sent", { state: { email: v.email } });
        return;
      }
      if (e instanceof ApiError && e.code === "AUTH_ACCOUNT_CLOSED") {
        setClosed({ email: v.email, purgeAfter: typeof e.problem.purgeAfter === "string" ? e.problem.purgeAfter : null });
        return;
      }
      setServerError(messageOf(e));
      if (e instanceof ApiError && e.code === "AUTH_LOGIN_LOCKED") {
        setLocked(v.email);
        return;
      }
      form.setFocus("password");
    }
  });

  if (noCompany) {
    return (
      <AuthCard notices title="ログイン">
        <AddCompanyPanel mode="login" email={noCompany.email} password={noCompany.password} otherProducts={noCompany.otherProducts} onBack={() => setNoCompany(undefined)} />
      </AuthCard>
    );
  }
  return (
    <AuthCard notices title="ログイン" onSubmit={submit}>
      {closed ? <ClosedNotice email={closed.email} purgeAfter={closed.purgeAfter} justClosed={!!justClosed && closed.email === justClosed.email} /> : null}
      <Field id={ids.mail} label="メールアドレス">
        {/* username webauthn：対応するブラウザでは欄を押すとパスキーの候補が出る（#113、Conditional UI） */}
        <input id={ids.mail} className="inp" type="email" inputMode="email" autoComplete="username webauthn" onKeyDown={guardComposingEnter} {...form.register("email")} />
      </Field>
      <Field id={ids.pw} label="パスワード">
        <PasswordInput id={ids.pw} autoComplete="current-password" reg={form.register("password")} />
      </Field>
      <ErrorText>{serverError ?? firstError ?? oauthError}</ErrorText>
      {locked !== null ? (
        <Link className="btn tonal" to="/forgot" state={{ email: locked }} data-testid="login-locked-reset">
          パスワード再設定
        </Link>
      ) : null}
      {/* 意味は、チェックのすぐ右の「？」へ（#92 決め8）。「？」は label の外に置く（吹き出しを押してもチェックが変わらないように） */}
      <div className="keeprow">
        <label className="chk" style={{ alignItems: "center" }}>
          <input type="checkbox" id={ids.keep} {...form.register("remember")} />
          <b>この端末ではログインしたままにする</b>
        </label>
        <Help testId="help-keep" text={P.copy.keepLogin} />
      </div>
      <button type="submit" className="btn primary big" disabled={form.formState.isSubmitting}>
        ログイン
      </button>
      {/* 左「パスワードをお忘れの方」（#287）・右「会員登録」。どちらも 44px の文字のボタンで縦の中心をそろえる（#120。PC版・スマホ版で同じ） */}
      <div className="authlinks">
        <Link className="btn text" to="/forgot">{MSG.forgotLink}</Link>
        <Link className="btn text" to="/signup">会員登録</Link>
      </div>
      {/* パスキー（#113）・Google・Apple（#112）。「ログインしたままにする」もそのまま渡す。パスキーに対応しないブラウザではボタンを出さない */}
      <OAuthButtons
        intent="login"
        remember={form.watch("remember") !== false}
        {...(next ? { after: next } : {})}
        before={
          pk.supported ? (
            <button type="button" className="btn oauthbtn passkey" disabled={pkBusy} onClick={() => void loginWithPasskey()} data-testid="passkey-login">
              <PasskeyIcon />
              <span>{PASSKEY_MSG.loginButton}</span>
            </button>
          ) : null
        }
      />
      {/* ログインできないときの窓口（#124 の 3a）。ログイン前のお問い合わせ */}
      <p className="authcontact">
        {SUPPORT_MSG.loginTrouble}
        <Link className="btn text" to="/contact" data-testid="login-contact">{SUPPORT_MSG.title}</Link>
      </p>
    </AuthCard>
  );
}
